import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Badge } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { FaHandsHelping, FaSeedling, FaCalendarAlt, FaDollarSign, FaHeart } from "react-icons/fa";
import heroImage from "../assets/hero-placeholder.jpg";
import communityImage from "../assets/community.jpg";
import impactImage from "../assets/impact.jpg";

const Home = () => {
  const [fadeIn, setFadeIn] = useState(false);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setFadeIn(true), 200);

    setLatestNews([
      { _id: "1", title: "Community Meeting This Saturday", date: "2025-03-01" },
      { _id: "2", title: "Successful Food Drive Helps 200 Families", date: "2025-02-15" },
      { _id: "3", title: "Exciting Plans for the Annual Gala", date: "2025-02-10" },
    ]);
    setLoading(false);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section
        className="hero hero-section text-white text-center d-flex align-items-center position-relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 193, 7, 0.85), rgba(255, 152, 0, 0.9)), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          minHeight: "80vh",
          marginTop: "-80px",
          paddingTop: "160px",
          textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <h1 className={`display-2 fw-bold fade-in-up ${fadeIn ? 'visible' : ''}`}>
                Welcome to Mbaitu USA
              </h1>
              <p className={`lead fs-3 fade-in-up ${fadeIn ? 'visible' : ''}`} style={{ transitionDelay: "0.5s" }}>
                Connecting, Supporting & Growing Together
              </p>

              {/* Community Stats */}
              <Row className="mt-4 justify-content-center">
                <Col md={3} className="mb-3">
                  <div className="hero-stat-card">
                    <h3 className="fw-bold mb-0">45+</h3>
                    <p className="mb-0 small">Active Members</p>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="hero-stat-card">
                    <h3 className="fw-bold mb-0">100+</h3>
                    <p className="mb-0 small">Families Supported</p>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="hero-stat-card">
                    <h3 className="fw-bold mb-0">$25K+</h3>
                    <p className="mb-0 small">Raised for Community</p>
                  </div>
                </Col>
                <Col md={3} className="mb-3">
                  <div className="hero-stat-card">
                    <h3 className="fw-bold mb-0">5+</h3>
                    <p className="mb-0 small">Years of Service</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          <div className="mt-5">
            <Button
              variant="light"
              size="lg"
              className="me-3 shadow-lg px-4 py-3 fw-bold btn-outline-thick"
              onClick={() => navigate("/about")}
            >
              Join Our Community
            </Button>
            <Button
              variant="warning"
              size="lg"
              className="shadow-lg px-4 py-3 fw-bold btn-outline-thick"
              onClick={() => navigate("/events")}
            >
              Upcoming Events
            </Button>
          </div>
        </Container>
      </section>

      {/* Dynamic Upcoming Event Alert */}
      <section className="py-5 section-gold-light">
        <Container>
          <Row className="align-items-center text-center">
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-center mb-3">
                <FaCalendarAlt className="text-warning me-3" size={32} />
                <div>
                  <Badge bg="warning" className="fs-6 px-3 py-2 badge-pulse">UPCOMING EVENTS</Badge>
                </div>
              </div>
              <h4 className="fw-bold text-dark mb-3">Stay Connected with Our Community</h4>
              <p className="text-muted mb-4">
                Don't miss our community gatherings, quarterly meetings, and special events.
                Check out what's coming up and RSVP to join us!
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  variant="warning"
                  size="lg"
                  className="fw-bold shadow-sm px-4"
                  onClick={() => navigate("/events")}
                >
                  View All Events
                </Button>
                <Button
                  variant="outline-warning"
                  size="lg"
                  className="fw-bold px-4"
                  onClick={() => navigate("/contact")}
                >
                  Get Notified
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Who We Are Section */}
      <section className="about-us bg-light py-5">
        <Container>
          <h2 className="text-center mb-4 fw-bold section-heading">Who We Are</h2>
          <p className="text-center text-muted px-md-5 fs-5">
            We are a growing organization of progressive Kenyans in Texas, united by a vision of support, cultural preservation, and economic empowerment.
            Our mission goes beyond assistance; we aim to build a strong and thriving community where everyone feels valued and supported.
          </p>

          <Row className="mt-5">
            <Col md={6} className="mb-4">
              <div className="h-100 p-4 bg-white rounded shadow-sm card-hover">
                <h4 className="fw-bold text-primary">Our Mission</h4>
                <p className="text-muted">
                  To unite the Kamba community in DFW and Texas, creating a sustainable solution to eliminate frequent burial fundraisers while promoting
                  a vibrant social, cultural, and economic foundation for all members.
                </p>
              </div>
            </Col>
            <Col md={6} className="mb-4">
              <div className="h-100 p-4 bg-white rounded shadow-sm card-hover">
                <h4 className="fw-bold text-primary">Our Vision</h4>
                <p className="text-muted">
                  To reduce the impact of social burdens and accelerate the cultural integration of the Kamba community within the broader American society,
                  ensuring a brighter future for current and future generations.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Impact & Initiatives */}
      <section className="py-5 section-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4">
              <img
                src={impactImage}
                alt="Community Impact"
                className="img-fluid rounded-3 shadow"
                style={{width: "100%", height: "400px", objectFit: "cover"}}
              />
            </Col>
            <Col lg={6}>
              <h2 className="fw-bold mb-4 section-heading">
                Our Impact & Initiatives
              </h2>
              <p className="text-muted fs-5 mb-4">
                Mbaitu USA is dedicated to creating positive change through community-driven initiatives,
                ensuring that no one walks alone in times of need.
              </p>

              <Row className="g-4">
                <Col sm={6}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                      <FaHandsHelping className="text-warning" size={24} />
                    </div>
                    <div>
                      <h6 className="fw-bold text-primary">Food Drives</h6>
                      <p className="text-muted small mb-0">Providing essential food supplies to families in need.</p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                      <FaDollarSign className="text-warning" size={24} />
                    </div>
                    <div>
                      <h6 className="fw-bold text-primary">Bereavement Support</h6>
                      <p className="text-muted small mb-0">Standing together during times of grief and crisis.</p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                      <FaSeedling className="text-warning" size={24} />
                    </div>
                    <div>
                      <h6 className="fw-bold text-primary">Cultural Events</h6>
                      <p className="text-muted small mb-0">Celebrating our heritage through festivals and networking.</p>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="d-flex align-items-start">
                    <div className="p-3 bg-warning bg-opacity-25 rounded-3 me-3">
                      <FaHeart className="text-warning" size={24} />
                    </div>
                    <div>
                      <h6 className="fw-bold text-primary">Family Support</h6>
                      <p className="text-muted small mb-0">Building stronger families through community care.</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Community Testimonials */}
      <section className="py-5 bg-white">
        <Container>
          <h2 className="text-center mb-5 fw-bold section-heading">
            What Our Members Say
          </h2>
          <Row className="g-4">
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100 card-lift">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <img
                      src={communityImage}
                      alt="Member"
                      className="rounded-circle"
                      style={{width: "80px", height: "80px", objectFit: "cover"}}
                    />
                  </div>
                  <blockquote className="fst-italic text-muted mb-3">
                    "Mbaitu USA has been a lifeline for our family. The support during difficult times and the sense of community is incredible."
                  </blockquote>
                  <footer className="blockquote-footer">
                    <strong>Sarah M.</strong>, Member since 2020
                  </footer>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100 card-lift">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <img
                      src={communityImage}
                      alt="Member"
                      className="rounded-circle"
                      style={{width: "80px", height: "80px", objectFit: "cover"}}
                    />
                  </div>
                  <blockquote className="fst-italic text-muted mb-3">
                    "The cultural events help us stay connected to our roots while building new friendships here in Texas."
                  </blockquote>
                  <footer className="blockquote-footer">
                    <strong>John K.</strong>, Member since 2019
                  </footer>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100 card-lift">
                <Card.Body className="text-center p-4">
                  <div className="mb-3">
                    <img
                      src={communityImage}
                      alt="Member"
                      className="rounded-circle"
                      style={{width: "80px", height: "80px", objectFit: "cover"}}
                    />
                  </div>
                  <blockquote className="fst-italic text-muted mb-3">
                    "Being part of Mbaitu means never facing life's challenges alone. This community truly cares."
                  </blockquote>
                  <footer className="blockquote-footer">
                    <strong>Grace W.</strong>, Member since 2021
                  </footer>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action Section */}
      <section className="py-5 section-gold-gradient">
        <Container>
          <Row className="text-center text-white">
            <Col lg={8} className="mx-auto">
              <h2 className="fw-bold mb-4">Ready to Join Our Community?</h2>
              <p className="fs-5 mb-4">
                Become part of a supportive network that stands together through life's journey.
                Together, we build stronger families and a brighter future.
              </p>
              <Row className="justify-content-center g-3">
                <Col md={4}>
                  <Button
                    variant="light"
                    size="lg"
                    className="w-100 fw-bold shadow"
                    onClick={() => navigate("/contact")}
                  >
                    Get In Touch
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="w-100 fw-bold btn-outline-thick"
                    onClick={() => navigate("/events")}
                  >
                    Attend an Event
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="w-100 fw-bold btn-outline-thick"
                    onClick={() => navigate("/about")}
                  >
                    Learn More
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Latest News & Updates */}
      <section className="news bg-white py-5">
        <Container>
          <h2 className="text-center mb-4 fw-bold section-heading">
            Latest News & Updates
          </h2>

          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Fetching news updates...</p>
            </div>
          ) : (
            <>
              {latestNews.length > 0 && (
                <Card className="mb-4 shadow border-0 rounded-3 mx-auto card-hover" style={{ maxWidth: "550px" }}>
                  <Card.Body className="text-center">
                    <h5 className="fw-bold mb-2">{latestNews[0].title}</h5>
                    <p className="text-muted small">{new Date(latestNews[0].date).toLocaleDateString()}</p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/events/${latestNews[0]._id}`)}
                    >
                      Read More
                    </Button>
                  </Card.Body>
                </Card>
              )}

              <Row className="g-3">
                {latestNews.slice(1).map((news) => (
                  <Col md={6} key={news._id}>
                    <Card className="shadow-sm border-0 rounded-3 h-100 card-hover">
                      <Card.Body>
                        <h6 className="fw-bold">{news.title}</h6>
                        <p className="text-muted small">{new Date(news.date).toLocaleDateString()}</p>
                        <div className="d-flex justify-content-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate(`/events/${news._id}`)}
                          >
                            Read More
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Container>
      </section>
    </>
  );
};

export default Home;
